// src/components/games/crossroads/GameInviteAcceptModal.tsx
import { logger } from '../../../utils/logger';
// Modal for accepting game invitations

import React, { useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonIcon,
  IonText,
  IonSelect,
  IonSelectOption,
  IonItem,
  IonLabel,
  IonSpinner,
  useIonToast,
} from '@ionic/react';
import { close, gameControllerOutline, checkmarkCircleOutline, closeCircleOutline } from 'ionicons/icons';
import { createMultiplayerGame } from '../../../services/crossroadsMultiplayerManager';
import { STARTER_DECKS } from './cardData';

interface GameInviteAcceptModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  invitationData: {
    fromUserId: string;
    fromUsername: string;
    fromDisplayName: string;
    challengerDeck: string;
    gameType: string;
  } | null;
  onGameCreated: (gameId: string, playerIndex: number) => void;
}

const DECK_OPTIONS = [
  { key: 'fire', name: 'Fire', emoji: '🔥', commander: 'Dice and Autism' },
  { key: 'water', name: 'Water', emoji: '💧', commander: 'Nic' },
  { key: 'earth', name: 'Earth', emoji: '🌱', commander: 'Carma' },
  { key: 'dark', name: 'Dark', emoji: '🌑', commander: 'Neal' },
  { key: 'light', name: 'Light', emoji: '☀️', commander: 'Brandy' },
  { key: 'fae', name: 'Fae', emoji: '✨', commander: 'Alli' },
  { key: 'wind', name: 'Wind', emoji: '💨', commander: 'Doug' },
];

export const GameInviteAcceptModal: React.FC<GameInviteAcceptModalProps> = ({
  isOpen,
  onDismiss,
  invitationData,
  onGameCreated,
}) => {
  const [selectedDeck, setSelectedDeck] = useState<string>('water');
  const [accepting, setAccepting] = useState(false);
  const [presentToast] = useIonToast();

  const handleAccept = async () => {
    if (!invitationData) return;

    setAccepting(true);

    try {
      // Get current user
      const { supabase } = await import('../../../services/supabase');
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        presentToast({
          message: 'You must be logged in to accept challenges',
          duration: 2000,
          color: 'danger',
        });
        setAccepting(false);
        return;
      }

      // Get deck data
      const challengerDeckKey = invitationData.challengerDeck as keyof typeof STARTER_DECKS;
      const myDeckKey = selectedDeck as keyof typeof STARTER_DECKS;

      const challengerDeck = STARTER_DECKS[challengerDeckKey] || STARTER_DECKS.fire;
      const myDeck = STARTER_DECKS[myDeckKey] || STARTER_DECKS.water;

      // Create game
      const result = await createMultiplayerGame(
        invitationData.fromUserId,
        user.id,
        challengerDeck,
        myDeck,
        invitationData.fromDisplayName,
        'You'
      );

      if (result.success && result.gameId) {
        presentToast({
          message: `Game created! Get ready to battle!`,
          duration: 2000,
          color: 'success',
        });

        // Join the game as player 2 (index 1)
        onGameCreated(result.gameId, 1);
        onDismiss();
      } else {
        presentToast({
          message: result.error || 'Failed to create game',
          duration: 2000,
          color: 'danger',
        });
      }
    } catch (error) {
      logger.error('Error accepting challenge:', error);
      presentToast({
        message: 'Failed to accept challenge',
        duration: 2000,
        color: 'danger',
      });
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = () => {
    presentToast({
      message: 'Challenge declined',
      duration: 1500,
      color: 'medium',
    });
    onDismiss();
  };

  if (!invitationData) return null;

  const challengerDeckInfo = DECK_OPTIONS.find(d => d.key === invitationData.challengerDeck);

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Game Challenge</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss} disabled={accepting}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: '16px' }}>
          <IonText>
            <h2>
              <IonIcon icon={gameControllerOutline} /> Crossroads TCG Challenge!
            </h2>
          </IonText>

          <div
            style={{
              background: 'var(--ion-color-primary-tint)',
              padding: '16px',
              borderRadius: '8px',
              margin: '16px 0',
            }}
          >
            <IonText>
              <h3 style={{ margin: '0 0 8px 0' }}>
                {invitationData.fromDisplayName} challenges you!
              </h3>
              <p style={{ margin: '0' }}>
                <strong>Their Deck:</strong> {challengerDeckInfo?.emoji} {challengerDeckInfo?.name} ({challengerDeckInfo?.commander})
              </p>
            </IonText>
          </div>

          <IonItem>
            <IonLabel>Choose Your Deck</IonLabel>
            <IonSelect
              value={selectedDeck}
              onIonChange={(e) => setSelectedDeck(e.detail.value)}
              interface="popover"
              disabled={accepting}
            >
              {DECK_OPTIONS.map((deck) => (
                <IonSelectOption key={deck.key} value={deck.key}>
                  {deck.emoji} {deck.name} - {deck.commander}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>

          <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
            <IonButton
              expand="block"
              color="success"
              onClick={handleAccept}
              disabled={accepting}
              style={{ flex: 1 }}
            >
              {accepting ? (
                <>
                  <IonSpinner slot="start" />
                  Creating Game...
                </>
              ) : (
                <>
                  <IonIcon icon={checkmarkCircleOutline} slot="start" />
                  Accept Challenge
                </>
              )}
            </IonButton>

            <IonButton
              expand="block"
              color="danger"
              fill="outline"
              onClick={handleDecline}
              disabled={accepting}
              style={{ flex: 1 }}
            >
              <IonIcon icon={closeCircleOutline} slot="start" />
              Decline
            </IonButton>
          </div>

          <IonText color="medium">
            <p style={{ fontSize: '0.9em', textAlign: 'center', marginTop: '16px' }}>
              The game will start immediately after you accept. You'll be player 2, so {invitationData.fromDisplayName} goes first!
            </p>
          </IonText>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default GameInviteAcceptModal;
